// @ts-strict-ignore
import { AppComponentProps } from '../typescriptHelpers';
import * as React from 'react';
import { tryUpdateAppSettings, SetAdditionalBranchesRequest } from '../actions/appSettings';
import { RootState } from '../reducers/reducers';
import { getError, getIsLoading, getUpdated } from '../helpers';
import { AppSettingsUpdatable } from '../actions/updatable';
import { useDispatch, useSelector } from 'react-redux';
import { antGrey, runwayColors } from './styles/globalStyles';
import styles from './styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Modal from 'antd/lib/modal/Modal';
import Form, { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input/Input';
import { BASE_BRANCH_FIELD_RULES, formValidAndRequiredFieldsNotEmpty } from '../helpers/formHelpers';
import Descriptions from 'antd/lib/descriptions';
import Space from 'antd/lib/space';
import { isEmpty } from 'lodash';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { selectAppSettings, selectAuthCredentials, selectIsWorkflowsMigratedAndEnabled } from '../selectors/selectors';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { globalMessage } from '../utils/globalMessage';
import * as API from '../api';
import { AppEntityRequest } from '../actions/entities';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import Alert from 'antd/lib/alert';
import Typography from 'antd/lib/typography';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

export const WORKING_BRANCH_FIELD_RULES = [
    ...BASE_BRANCH_FIELD_RULES,
    {
        required: true,
        message: 'Working branch is required',
    },
];

interface AdditionalBranchesModalProps {
    appId: string;
    isVisible: boolean;
    isLoading: boolean;
    isLoadingWorkingBranchGuess: boolean;
    workingBranchGuess?: string;
    onSave: (workingBranch?: string, stagingBranch?: string, prodBranch?: string) => void;
    onCancel: () => void;
}

const AdditionalBranchesModal: React.FC<AdditionalBranchesModalProps> = (props: AdditionalBranchesModalProps) => {
    const [form] = useForm();
    const [formValid, setFormValid] = React.useState(false);

    const initialWorkingBranch = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.workingBranch;
    });

    const initialStagingBranch = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.stagingBranch;
    });

    const initialProdBranch = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.prodBranch;
    });

    const isWorkflowsMigratedAndEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsMigratedAndEnabled(state, props.appId),
    );

    // FP apps no longer offer staging/deploy overrides, but ones already set stay visible so they can be cleared.
    const showStagingBranch = !isWorkflowsMigratedAndEnabled || !!initialStagingBranch;
    const showProdBranch = !isWorkflowsMigratedAndEnabled || !!initialProdBranch;

    const releaseBranchPatterns = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.releaseBranchPatterns.map((releaseBranchPattern) => releaseBranchPattern.pattern);
    });

    const onSubmit = () => {
        form.validateFields().then((values) => {
            const workingBranch =
                values.workingBranch ?
                    !isEmpty(values.workingBranch) ?
                        values.workingBranch
                    :   null
                :   null;

            const stagingBranch =
                values.stagingBranch ?
                    !isEmpty(values.stagingBranch) ?
                        values.stagingBranch
                    :   null
                :   null;

            const prodBranch =
                values.prodBranch ?
                    !isEmpty(values.prodBranch) ?
                        values.prodBranch
                    :   null
                :   null;

            props.onSave(workingBranch, stagingBranch, prodBranch);
        });
    };

    const onFieldsChange = () => {
        const valid = formValidAndRequiredFieldsNotEmpty(form, []);

        setFormValid(valid);
    };

    const onApplySuggestionButton = () => {
        form.setFieldsValue({ workingBranch: props.workingBranchGuess });
        onFieldsChange();
    };

    const branchValidationFunction = (value: string, branchType: string) => {
        if (value && value.includes(' ')) {
            return Promise.reject('Branch cannot contain a space');
        }

        if (value && releaseBranchPatterns.length > 0) {
            const valueEqualsReleaseBranchPattern = releaseBranchPatterns.includes(value);

            if (valueEqualsReleaseBranchPattern) {
                return Promise.reject(`${branchType} cannot be the same as one of your release branches`);
            }
        }

        return Promise.resolve();
    };

    React.useEffect(() => {
        if (props.isVisible) {
            const initialValues = {
                workingBranch: initialWorkingBranch,
                stagingBranch: initialStagingBranch,
                prodBranch: initialProdBranch,
            };

            form.setFieldsValue(initialValues);
        }
    }, [props.isVisible]);

    React.useEffect(() => {
        return;
    }, [props.isLoadingWorkingBranchGuess, props.workingBranchGuess]);

    return (
        <Modal
            title="Additional branch settings"
            open={props.isVisible}
            okButtonProps={{ loading: props.isLoading, disabled: !formValid }}
            okText="Save"
            onOk={() => form.submit()}
            onCancel={props.onCancel}
            destroyOnClose
        >
            <Form
                form={form}
                onFinish={onSubmit}
                onFieldsChange={onFieldsChange}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 16, offset: 1 }}
                labelAlign="left"
            >
                <FormItem
                    name="workingBranch"
                    label="Working branch"
                    rules={WORKING_BRANCH_FIELD_RULES}
                    extra={
                        <>
                            Your main development working branch (e.g. main)
                            {props.isLoadingWorkingBranchGuess && (
                                <p>
                                    <Spin size="small" />
                                </p>
                            )}
                            {props.workingBranchGuess && (
                                <Alert
                                    style={{
                                        marginTop: '1em',
                                        marginBottom: '1em',
                                        backgroundColor: `${runwayColors.primary}10`,
                                        border: `1px solid ${runwayColors.primary}50`,
                                    }}
                                    message={
                                        <>
                                            Suggested:{' '}
                                            <Text code style={{ margin: '1em' }}>
                                                {props.workingBranchGuess}
                                            </Text>
                                            <TooltipInfoIcon tooltipContent="Suggestion for this field based on the branches we found in your repository" />
                                        </>
                                    }
                                    action={
                                        <Button size="small" onClick={onApplySuggestionButton}>
                                            Apply
                                        </Button>
                                    }
                                />
                            )}
                        </>
                    }
                >
                    <Input autoComplete="off" />
                </FormItem>

                {showStagingBranch && (
                    <FormItem
                        name="stagingBranch"
                        label="Staging branch"
                        rules={[
                            {
                                validator: (rule, value) => {
                                    return branchValidationFunction(value, 'Staging branch');
                                },
                            },
                        ]}
                        extra="Only set this if you create Release Candidates from a branch other than your release branch"
                    >
                        <Input autoComplete="off" />
                    </FormItem>
                )}
                {showProdBranch && (
                    <FormItem
                        name="prodBranch"
                        label="Deploy branch"
                        rules={[
                            {
                                validator: (rule, value) => {
                                    return branchValidationFunction(value, 'Deploy branch');
                                },
                            },
                        ]}
                        extra="Only set this if you create final builds from a branch other than your release branch"
                    >
                        <Input autoComplete="off" />
                    </FormItem>
                )}
                <Text type="secondary">
                    Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </Text>
            </Form>
        </Modal>
    );
};

const AdditionalBranchesComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const [additionalBranchesModalVisible, setAdditionalBranchesModalVisible] = React.useState(false);
    const [suggestedWorkingBranch, setSuggestedWorkingBranch] = React.useState('');
    const [isLoadingWorkingBranchGuess, setIsLoadingWorkingBranchGuess] = React.useState(false);

    const setAdditionalBranchesIsLoading = useSelector((state: RootState) =>
        getIsLoading(AppSettingsUpdatable.SetAdditionalBranches, state),
    );
    const setAdditionalBranchesError = useSelector((state: RootState) =>
        getError(AppSettingsUpdatable.SetAdditionalBranches, state),
    );
    const setAdditionalBranchesSuccess = useSelector((state: RootState) =>
        getUpdated(AppSettingsUpdatable.SetAdditionalBranches, state),
    );

    const workingBranch = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.workingBranch;
    });

    const stagingBranch = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.stagingBranch;
    });

    const prodBranch = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.prodBranch;
    });

    const dispatch = useDispatch();

    const onEditAdditionalBranchesClicked = () => {
        setAdditionalBranchesModalVisible(true);
        fetchGuessedWorkingBranch();
    };

    const onCancelEditAdditionalBranches = () => {
        setAdditionalBranchesModalVisible(false);
    };

    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));

    const fetchGuessedWorkingBranch = () => {
        if (!workingBranch && !suggestedWorkingBranch) {
            const request: AppEntityRequest = {
                orgId: props.orgId,
                appId: props.appId,
            };

            setIsLoadingWorkingBranchGuess(true);

            API.getSuggestedWorkingBranch(request, authCreds)
                .then((response) => {
                    setSuggestedWorkingBranch(response);
                })
                .catch(() => {
                    return;
                })
                .finally(() => {
                    setIsLoadingWorkingBranchGuess(false);
                });
        }
    };

    const saveAdditionalBranches = (workingBranch?: string, stagingBranch?: string, prodBranch?: string) => {
        const request: SetAdditionalBranchesRequest = {
            orgId: props.orgId,
            appId: props.appId,
            workingBranch,
            stagingBranch,
            prodBranch,
        };

        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.SetAdditionalBranches, request));
    };

    React.useEffect(() => {
        if (setAdditionalBranchesError) {
            globalMessage.error(setAdditionalBranchesError);
        }
    }, [setAdditionalBranchesError]);

    React.useEffect(() => {
        if (setAdditionalBranchesSuccess) {
            setAdditionalBranchesModalVisible(false);
        }
    }, [setAdditionalBranchesSuccess]);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={styles.appSettingsSectionTitleStyle}>Additional branches</Text>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateAdditionalBranchConfig}
                    onClick={onEditAdditionalBranchesClicked}
                >
                    Edit
                </PermissionedActionButton>
            </div>
            <Descriptions
                size="middle"
                bordered
                style={{ ...styles.descriptionsWidthStyle, marginBottom: 18, marginTop: 18 }}
                column={1}
                labelStyle={{ ...styles.descriptionsLabelStyle }}
            >
                <Descriptions.Item
                    label={
                        <Space>
                            <Text style={{ color: runwayColors.darkTextGrey }}>Working branch</Text>
                            <TooltipInfoIcon tooltipContent="Your main development working branch" />
                        </Space>
                    }
                    contentStyle={workingBranch ? {} : { color: antGrey.grey6 }}
                >
                    {workingBranch ?? 'Not set'}
                </Descriptions.Item>
                {stagingBranch && (
                    <Descriptions.Item
                        label={
                            <Space>
                                <Text style={{ color: runwayColors.darkTextGrey }}>Staging branch</Text>
                                <TooltipInfoIcon tooltipContent="The branch you create release candidate builds from, if it's different than your release branch" />
                            </Space>
                        }
                        contentStyle={stagingBranch ? {} : { color: antGrey.grey6 }}
                    >
                        {stagingBranch ?? 'None'}
                    </Descriptions.Item>
                )}
                {prodBranch && (
                    <Descriptions.Item
                        label={
                            <Space>
                                <Text style={{ color: runwayColors.darkTextGrey }}>Deploy branch</Text>
                                <TooltipInfoIcon tooltipContent="The branch you create final builds from, if it's different than your release branch" />
                            </Space>
                        }
                        contentStyle={prodBranch ? {} : { color: antGrey.grey6 }}
                    >
                        {prodBranch ?? 'None'}
                    </Descriptions.Item>
                )}
            </Descriptions>
            <AdditionalBranchesModal
                appId={props.appId}
                isLoading={setAdditionalBranchesIsLoading}
                isVisible={additionalBranchesModalVisible}
                onSave={saveAdditionalBranches}
                onCancel={onCancelEditAdditionalBranches}
                workingBranchGuess={suggestedWorkingBranch}
                isLoadingWorkingBranchGuess={isLoadingWorkingBranchGuess}
            />
        </div>
    );
};

export default AdditionalBranchesComponent;
